import { FiMic } from 'react-icons/fi';

export const advancedDiscourseData = {
  courseId: 'advanced-discourse',
  courseType: 'speaking-course',
  title: 'Advanced Discourse Markers',
  icon: FiMic,
  overview: {
    impact: 'Move beyond basic linking words to sophisticated markers that make your speech sound natural, organised, and impressively fluent',
    focus: 'Markers for clarification, digression, emphasis, and attitude — and how to use them without sounding scripted',
    uses: 'Speaking Parts 2 and 3, where extended answers demand smooth transitions and natural flow',
    duration: '25 minutes',
  },

  sections: [
    {
      title: 'What Are Discourse Markers?',
      content: `<p>Discourse markers are words and phrases that <strong>organise, connect, and direct</strong> the flow of speech. Basic markers like "firstly", "however", and "in addition" are familiar to most IELTS candidates. Advanced markers — "what I mean is", "as a matter of fact", "interestingly" — are what separate Band 7 speakers from Band 8+ speakers.</p>
<p>Unlike grammar or vocabulary, discourse markers signal <em>how</em> you are thinking: whether you are clarifying, digressing, emphasising, or expressing attitude. They are the invisible architecture of fluent speech.</p>`,
      points: [
        'Basic markers (Band 5–6): firstly, secondly, however, also, in conclusion.',
        'Intermediate markers (Band 6–7): on the other hand, having said that, as a result, in contrast.',
        'Advanced markers (Band 7–8+): what I mean is, in other words, by the way, as a matter of fact, frankly speaking, interestingly enough.',
        'Overusing markers sounds rehearsed — the goal is natural integration, not a checklist.',
        'Native speakers use discourse markers constantly without thinking; practise until they become instinctive.',
      ],
      examples: [
        { label: 'Basic', text: 'Firstly, I like reading. Secondly, I enjoy cooking. However, I don\'t have much free time.' },
        { label: 'Advanced', text: 'I\'m quite into reading, actually. What I mean is, I try to read every evening before bed. And interestingly, I\'ve found that it helps me sleep better too.' },
      ],
    },
    {
      title: 'Clarification & Reformulation Markers',
      content: `<p>These markers allow you to <strong>rephrase, clarify, or correct</strong> yourself mid-answer — a skill that examiners reward because it shows you can self-monitor and refine your message in real time.</p>`,
      points: [
        '"What I mean is..." — clarifies a point that might have been unclear.',
        '"In other words..." — rephrases the same idea more simply or precisely.',
        '"That is to say..." — formal equivalent of "in other words".',
        '"What I\'m trying to say is..." — signals self-correction, shows awareness.',
        '"To put it another way..." — offers an alternative angle on the same point.',
        '"Or rather..." — corrects a word or phrase you just used.',
      ],
      table: {
        headers: ['Marker', 'Function', 'Example in Context'],
        rows: [
          ['What I mean is...', 'Clarification', 'Travel is enriching — what I mean is, it opens your mind to different ways of living.'],
          ['In other words...', 'Reformulation', 'The exam tests communicative competence. In other words, can you get your message across clearly?'],
          ['That is to say...', 'Formal rephrasing', 'The policy has been ineffective. That is to say, it hasn\'t achieved any of its stated goals.'],
          ['Or rather...', 'Self-correction', 'I studied medicine — or rather, I started studying medicine but switched to biology.'],
          ['To put it another way...', 'Alternative angle', 'University isn\'t for everyone. To put it another way, vocational training can be equally valuable.'],
        ],
      },
      warning: 'Don\'t overuse "what I mean is" — once or twice per speaking test is natural; more than that sounds like a crutch.',
    },
    {
      title: 'Digression, Return & Emphasis Markers',
      content: `<p><strong>Digression markers</strong> let you introduce a related side point without losing your thread. <strong>Return markers</strong> bring you back. <strong>Emphasis markers</strong> signal that a point is especially important. Together they create a natural, conversational rhythm.</p>`,
      points: [
        'Digression: "By the way...", "Incidentally...", "Speaking of which...", "That reminds me..."',
        'Return: "Anyway...", "Going back to what I was saying...", "As I was saying...", "But to get back to the point..."',
        'Emphasis: "Indeed...", "As a matter of fact...", "In fact...", "The point is..."',
        'Digression + Return is a powerful combo: it shows the examiner you can manage complex speech flow.',
      ],
      table: {
        headers: ['Type', 'Marker', 'Example'],
        rows: [
          ['Digression', 'By the way,', 'By the way, this reminds me of something my teacher once said about language learning.'],
          ['Digression', 'Incidentally,', 'Incidentally, Japan has one of the highest literacy rates in the world.'],
          ['Digression', 'Speaking of which,', 'Speaking of which, I read an interesting article about this just last week.'],
          ['Return', 'Anyway,', 'Anyway, to answer your question, I think education is the key factor.'],
          ['Return', 'Going back to the point,', 'Going back to the point, I believe governments should invest more in public health.'],
          ['Return', 'As I was saying,', 'As I was saying, technology has both advantages and disadvantages.'],
          ['Emphasis', 'As a matter of fact,', 'As a matter of fact, research consistently supports this view.'],
          ['Emphasis', 'Indeed,', 'Indeed, this is one of the most pressing issues of our time.'],
          ['Emphasis', 'The point is,', 'The point is, without funding, none of these proposals will succeed.'],
        ],
      },
    },
    {
      title: 'Attitude Markers and Natural Flow',
      content: `<p><strong>Attitude markers</strong> reveal your personal stance or emotional response to a topic. They add authenticity and personality to your answers — qualities that examiners associate with genuine communicative competence at Band 8+.</p>
<p>The key to natural flow is <strong>combining</strong> different marker types within a single extended answer, the same way a native speaker would.</p>`,
      points: [
        '"Frankly..." / "To be honest..." — signals candour ("Frankly, I think the education system needs a complete overhaul.").',
        '"Honestly..." — slightly more informal ("Honestly, I\'m not sure there\'s a simple answer.").',
        '"Interestingly..." / "Surprisingly..." — signals unexpected information ("Interestingly, Finland has no standardised testing and yet tops global education rankings.").',
        '"Admittedly..." — concedes a point ("Admittedly, there are some drawbacks to working from home.").',
        '"Understandably..." — shows empathy ("Understandably, many parents worry about screen time.").',
      ],
      examples: [
        { label: 'Natural flow (combined markers)', text: 'Honestly, I think social media has done more harm than good for teenagers. What I mean is, it creates unrealistic expectations about appearance and success. Interestingly, I read a study last month that linked Instagram use to increased anxiety among 14 to 18-year-olds. By the way, my younger sister experienced something similar — she became quite anxious about her image online. Anyway, going back to the main point, I believe schools need to teach digital literacy from an early age.' },
      ],
      warning: 'Natural flow means 2–4 varied markers in a 45-second answer, not one in every sentence. If it sounds like a checklist, you have overdone it.',
    },
  ],

  exercises: [
    {
      title: 'Identify the Function of Discourse Markers',
      type: 'select',
      instructions: 'Choose the function that each discourse marker serves in the given sentence.',
      questions: [
        {
          text: '"I love living in the city. By the way, did you know that Tokyo is the most populated city in the world?"',
          options: ['Clarification', 'Digression', 'Emphasis', 'Self-correction'],
          answer: 'Digression',
          explanation: '"By the way" introduces a related but tangential piece of information — a classic digression marker.',
        },
        {
          text: '"Education is important. Indeed, it is arguably the most important investment a country can make."',
          options: ['Digression', 'Return', 'Emphasis', 'Attitude'],
          answer: 'Emphasis',
          explanation: '"Indeed" strengthens and underlines the previous statement, serving as an emphasis marker.',
        },
        {
          text: '"I think remote work is great — or rather, it\'s great for certain types of jobs."',
          options: ['Clarification / Self-correction', 'Digression', 'Emphasis', 'Attitude'],
          answer: 'Clarification / Self-correction',
          explanation: '"Or rather" corrects or refines the previous statement, functioning as a self-correction marker.',
        },
        {
          text: '"Frankly, I think the government hasn\'t done enough to address climate change."',
          options: ['Clarification', 'Return', 'Emphasis', 'Attitude'],
          answer: 'Attitude',
          explanation: '"Frankly" signals the speaker\'s personal stance and candour — an attitude marker showing honest opinion.',
        },
      ],
    },
    {
      title: 'Insert the Right Marker',
      type: 'fill-in',
      instructions: 'Fill in the blank with the most appropriate discourse marker from context.',
      questions: [
        {
          text: 'I think travelling is wonderful. ___, it teaches you things that no textbook ever could.',
          answer: 'In fact',
          explanation: '"In fact" adds emphasis by reinforcing and strengthening the initial claim about travelling.',
        },
        {
          text: 'I was talking about education spending. ___, I saw an interesting documentary about Finnish schools. But ___, I believe funding is the key issue.',
          answer: 'By the way',
          explanation: '"By the way" introduces the digression about the documentary. The second blank needs "anyway" or "going back to the point" to return to the main topic.',
        },
        {
          text: 'The internet has changed everything. ___, it has made information accessible to billions who previously had no access.',
          answer: 'What I mean is',
          explanation: '"What I mean is" clarifies the broad claim ("changed everything") with a specific aspect (information access).',
        },
        {
          text: '___, I don\'t think there\'s a simple solution to the housing crisis.',
          answer: 'To be honest',
          explanation: '"To be honest" signals the speaker\'s candid personal view — an attitude marker that adds authenticity.',
        },
      ],
    },
    {
      title: 'Choose Natural-Sounding Responses',
      type: 'select',
      instructions: 'Select the response that uses discourse markers most naturally.',
      questions: [
        {
          text: 'Question: "Do you think university education is necessary for success?"',
          options: [
            'Firstly, yes. Secondly, education is good. Thirdly, it helps you. In conclusion, yes.',
            'To be honest, I don\'t think university is necessary for everyone. What I mean is, it depends on the career path. For example, in tech, many successful people are self-taught. Having said that, for professions like medicine, a degree is obviously essential.',
            'However, firstly, on the other hand, in addition, therefore, university is good.',
          ],
          answer: 'To be honest, I don\'t think university is necessary for everyone. What I mean is, it depends on the career path. For example, in tech, many successful people are self-taught. Having said that, for professions like medicine, a degree is obviously essential.',
          explanation: 'This answer uses attitude ("To be honest"), clarification ("What I mean is"), and balancing ("Having said that") naturally. The others mechanically list markers or string them together meaninglessly.',
        },
        {
          text: 'Question: "How has technology affected communication?"',
          options: [
            'Technology is good and bad.',
            'Well, interestingly, technology has both improved and complicated communication. As a matter of fact, we can now video-call anyone in the world instantly. But honestly, I\'ve noticed that face-to-face conversations seem to be declining among younger people. Anyway, I think the overall impact is positive, provided we use technology mindfully.',
            'Firstly technology. Secondly communication. However both. In conclusion yes.',
          ],
          answer: 'Well, interestingly, technology has both improved and complicated communication. As a matter of fact, we can now video-call anyone in the world instantly. But honestly, I\'ve noticed that face-to-face conversations seem to be declining among younger people. Anyway, I think the overall impact is positive, provided we use technology mindfully.',
          explanation: 'This answer weaves attitude ("honestly"), emphasis ("as a matter of fact"), and return ("anyway") into a natural, flowing response.',
        },
        {
          text: 'Question: "Should governments spend more on the arts?"',
          options: [
            'Frankly, I believe the arts are chronically underfunded. What I mean is, governments tend to prioritise economic growth over cultural investment. Interestingly, countries that do invest heavily in the arts — like France — often see significant tourism revenue as a result. So in other words, arts funding can actually benefit the economy.',
            'I think arts are important. Also they are nice. However money is limited. In conclusion, maybe.',
            'Indeed furthermore moreover as a matter of fact frankly yes.',
          ],
          answer: 'Frankly, I believe the arts are chronically underfunded. What I mean is, governments tend to prioritise economic growth over cultural investment. Interestingly, countries that do invest heavily in the arts — like France — often see significant tourism revenue as a result. So in other words, arts funding can actually benefit the economy.',
          explanation: 'Attitude ("frankly"), clarification ("what I mean is"), attitude again ("interestingly"), and reformulation ("in other words") — varied, purposeful, and natural.',
        },
        {
          text: 'Question: "Is competition good for children?"',
          options: [
            'Competition is good. It motivates children.',
            'Admittedly, competition can sometimes put too much pressure on young children. But as a matter of fact, a healthy level of competition teaches resilience. Speaking of which, I remember sports day at my primary school — it was stressful but also thrilling. Going back to the point, I think the key is balance: competition should encourage effort, not fear of failure.',
            'Firstly, competition. Secondly, children. However, pressure. In addition, motivation.',
          ],
          answer: 'Admittedly, competition can sometimes put too much pressure on young children. But as a matter of fact, a healthy level of competition teaches resilience. Speaking of which, I remember sports day at my primary school — it was stressful but also thrilling. Going back to the point, I think the key is balance: competition should encourage effort, not fear of failure.',
          explanation: 'This uses concession ("admittedly"), emphasis ("as a matter of fact"), digression ("speaking of which"), and return ("going back to the point") — all flowing naturally in a well-structured answer.',
        },
      ],
    },
    {
      title: 'Add Discourse Markers to Flat Responses',
      type: 'writing',
      instructions: 'The response below sounds flat and robotic. Rewrite it by adding 2–3 appropriate discourse markers to make it sound natural and fluent.',
      questions: [
        {
          text: 'Original: "I think reading is important. It improves vocabulary. It also helps with critical thinking. Some people don\'t like reading. That\'s their choice."',
          example: '"Honestly, I think reading is incredibly important. What I mean is, it doesn\'t just improve vocabulary — it also sharpens critical thinking skills. Admittedly, some people don\'t enjoy reading, and that\'s their choice. But as a matter of fact, even reading short articles or blogs can make a difference."',
          tip: 'Add attitude markers (honestly, admittedly), clarification (what I mean is), and emphasis (as a matter of fact) to create natural flow.',
        },
        {
          text: 'Original: "Social media has changed how we communicate. People use it every day. It can be positive or negative. Companies also use social media for marketing."',
          example: '"Interestingly, social media has completely transformed how we communicate — most people use it daily without even thinking about it. Frankly, I think the effects are mixed: it connects us but can also be quite superficial. By the way, it\'s worth noting that companies have seized on this too, using platforms for targeted marketing. Anyway, the key point is that social media is neither purely good nor bad — it depends on how we use it."',
          tip: 'Use digression (by the way) and return (anyway) to manage an aside, plus attitude markers (interestingly, frankly) for personality.',
        },
        {
          text: 'Original: "Travelling is expensive. Not everyone can afford it. It teaches you about different cultures. I think it is valuable."',
          example: '"To be honest, travelling can be quite expensive, and not everyone can afford it — that\'s a real barrier. Having said that, the cultural education you gain from travelling is hard to replicate in any other way. Indeed, I would argue that even a short trip to a neighbouring country can broaden your perspective enormously. In other words, the value of travel goes far beyond the price of a plane ticket."',
          tip: 'Use attitude (to be honest), emphasis (indeed), and reformulation (in other words) to add depth and fluency to the response.',
        },
      ],
    },
    {
      title: 'Match Markers to Functions',
      type: 'select',
      instructions: 'Match each discourse marker to its primary function.',
      questions: [
        {
          text: '"In other words" is primarily used for:',
          options: ['Digression', 'Reformulation / Clarification', 'Emphasis', 'Expressing attitude'],
          answer: 'Reformulation / Clarification',
          explanation: '"In other words" rephrases a previous statement in simpler or clearer terms — a reformulation marker.',
        },
        {
          text: '"As a matter of fact" is primarily used for:',
          options: ['Digression', 'Return', 'Emphasis', 'Self-correction'],
          answer: 'Emphasis',
          explanation: '"As a matter of fact" reinforces or strengthens a previous point with factual emphasis.',
        },
        {
          text: '"Anyway, going back to..." is primarily used for:',
          options: ['Clarification', 'Digression', 'Return to main point', 'Expressing attitude'],
          answer: 'Return to main point',
          explanation: '"Anyway, going back to..." signals the speaker is returning from a digression to the main topic.',
        },
      ],
    },
  ],

  tips: [
    'Aim for 2–4 varied discourse markers per extended answer — enough to show range, not so many that it sounds rehearsed.',
    'Practise the digression-return combo (by the way... anyway...) — examiners recognise this as a sign of fluent, confident speech.',
    'Use attitude markers (frankly, interestingly, admittedly) to add personality to your answers.',
    'Self-correction with "or rather" shows the examiner you monitor your own language — a Band 8 skill.',
    'Record yourself and listen back: do the markers sound forced or natural? Adjust until they flow.',
  ],
  dailyPractice: [
    'Pick one discourse marker per day and use it in at least five spoken sentences throughout the day.',
    'Watch an English-language interview and note every discourse marker used — categorise each as clarification, digression, return, emphasis, or attitude.',
    'Record a 2-minute response to a Part 2 cue card, then re-record it with at least three discourse markers added naturally.',
    'Practise the digression-return pair: deliberately go off-topic for one sentence, then bring yourself back using "anyway" or "going back to the point".',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Advanced Discourse Markers: This course teaches IELTS Speaking candidates how to go beyond basic linking words. Covers clarification markers (what I mean is, in other words), digression and return markers (by the way, anyway), emphasis markers (indeed, as a matter of fact), and attitude markers (frankly, interestingly, admittedly). Targets Band 8+ in Fluency & Coherence.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...advancedDiscourseData,
      title: advancedDiscourseData.title + ' (Foundations)',
      overview: { ...advancedDiscourseData.overview, duration: '15-20 minutes' },
      sections: advancedDiscourseData.sections.slice(0, Math.max(2, Math.ceil(advancedDiscourseData.sections.length / 2))),
      exercises: advancedDiscourseData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...advancedDiscourseData,
      title: advancedDiscourseData.title + ' (Advanced)',
      overview: { ...advancedDiscourseData.overview, duration: '30-35 minutes' },
      sections: advancedDiscourseData.sections,
      exercises: advancedDiscourseData.exercises.map(ex => ({ ...ex, title: ex.title + ' (Advanced)' })),
      tips: [
        ...(advancedDiscourseData.tips || []),
        'For Band 8+: Focus on natural-sounding delivery rather than just accuracy.',
        'Advanced tip: Integrate this skill naturally in timed IELTS speaking practice.',
      ],
    };
  }
  return advancedDiscourseData;
}
